import {useEffect} from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Traps Tab/Shift+Tab within containerRef while active, focuses the first
 * *visible* ref in initialFocusRefs (falling back to the first focusable
 * element) on activation, and returns focus to whatever was focused
 * beforehand once deactivated.
 *
 * initialFocusRefs takes a list because the preferred target is often a
 * responsive pair (a desktop-only and a mobile-only variant of the same
 * control) — only one of which is actually rendered visible at a time.
 */
const useFocusTrap = (active, containerRef, initialFocusRefs = []) => {
    useEffect(() => {
        if (!active || !containerRef.current) return undefined;

        const previouslyFocused = document.activeElement;
        const container = containerRef.current;
        const isVisible = (el) => !!el && el.offsetParent !== null;

        const focusables = () => Array.from(container.querySelectorAll(FOCUSABLE)).filter(isVisible);

        const preferred = initialFocusRefs.map((ref) => ref?.current).find(isVisible);
        const initialTarget = preferred || focusables()[0];
        if (initialTarget) initialTarget.focus();

        const onKeyDown = (e) => {
            if (e.key !== 'Tab') return;
            const els = focusables();
            if (els.length === 0) return;
            const first = els[0];
            const last = els[els.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            if (previouslyFocused && document.body.contains(previouslyFocused)) {
                previouslyFocused.focus();
            }
        };
    }, [active, containerRef, initialFocusRefs]);
};

export default useFocusTrap;
